import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Feature, Map } from "ol";
import Geometry from "ol/geom/Geometry";
import { FeatureLike } from "ol/Feature";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import Text from "ol/style/Text";

import { SwissMap, SwissMapProps, MapNavigator, Canton } from "components";
import { useVectorSource, useLocales, useIsMobile } from "hooks";
import { FireHistory } from "./FireHistory";

export type MapKind = "measures" | "warnMap";

const CITY_NAMES_PATH = "/qgis/city_names.json";

// Initial zoom is kept as float and zoom steps are int +/- 1
// Tolerance takes into account rounding errors on step calculation.
const ZOOM_TOLERANCE = 0.1;

interface Props {
  geojson?: string;
  background?: string;
  type: MapKind;
  legendLink: string;
  cantons: ReadonlyArray<Canton>;
  regionsPath: string;
  measuresJsonPath: string;
  filterPath: string;
  producedAt?: string;
}

export const FireMapDetail: React.FC<Props> = ({
  geojson,
  background,
  type,
  legendLink,
  cantons,
  regionsPath,
  measuresJsonPath,
  filterPath,
  producedAt,
}: Props) => {
  const { t } = useLocales();
  const {
    layer: vectorLayer,
    isReady: vectorReady,
    isError: vectorError,
  } = useVectorSource(geojson || "");
  const vectorFeatures = useMemo(
    () => (vectorReady && vectorLayer.getSource()?.getFeatures()) || [],
    [vectorReady, vectorLayer]
  );
  const { layer: textLayer, isError: textError } =
    useVectorSource(CITY_NAMES_PATH);

  const [selection, setSelection] = useState<Feature<Geometry> | null>(null);
  const [hovering, setHovering] = useState<Feature<Geometry> | null>(null);
  const isMobile = useIsMobile();
  const mapRef = React.useRef<Map | null>(null);
  const initialZoomRef = React.useRef<number | null>(null);

  const handleSelectFeature: SwissMapProps["onClick"] = (features) => {
    const [feature] = features;
    if (!features.length || selection?.getId() === feature?.getId()) {
      setSelection(null);
      return;
    }

    setSelection(feature);
  };

  const handleHoverFeature: SwissMapProps["onHover"] = (features) => {
    const [feature] = features;
    if (!features.length) {
      setHovering(null);
      return;
    }

    setHovering(feature);
  };

  // TODO: Define styles for both selected and hovered features
  // and make them type-depending (measures, warnMap).
  const mapStyles = useCallback(() => {
    const isSelected = (feature: FeatureLike) =>
      feature.getId() === selection?.getId();

    const isHovering = (feature: FeatureLike) =>
      feature.getId() === hovering?.getId();

    return (feature: FeatureLike) => {
      let strokeColor = "rgba(0, 0, 0, 0)";
      if (isSelected(feature)) {
        strokeColor = "rgba(0, 0, 0, 1)";
      } else if (isHovering(feature)) {
        strokeColor = "rgba(145, 145, 145, 1)";
      }

      return new Style({
        fill: new Fill({
          color: "rgba(0, 0, 0, 0)",
        }),
        stroke: new Stroke({
          color: strokeColor,
          width: 2,
        }),
      });
    };
  }, [selection, hovering]);

  const textStyles = useCallback(() => {
    return (feature: FeatureLike, res: number) => {
      const prio = feature.get("priority") as number;
      let showText = !isMobile;
      if (mapRef.current) {
        const initialZoom = initialZoomRef.current;
        const zoom = mapRef.current
          .getView()
          .getZoomForResolution(res) as number;

        if (
          (!isMobile &&
            initialZoom &&
            Math.abs(initialZoom - zoom) < ZOOM_TOLERANCE) ||
          (isMobile &&
            initialZoom !== null &&
            zoom > initialZoom + ZOOM_TOLERANCE)
        ) {
          showText = prio === 1;
        }
      }

      return new Style({
        text: new Text({
          text: showText ? feature.get("name") : "",
          font: '"Frutiger Neue",Helvetica,Arial,sans-serif"',
        }),
      });
    };
  }, [isMobile]);

  useEffect(() => {
    vectorLayer.setStyle(mapStyles());
  }, [vectorLayer, mapStyles]);

  useEffect(() => {
    textLayer.setStyle(textStyles());
  }, [textLayer, textStyles]);

  const handleMapInitialized = (map: Map) => {
    mapRef.current = map;
    initialZoomRef.current = map.getView().getZoom() || 0;
  };

  const handleNavigatorSelectionChange = useCallback(
    (regionId: number | null) => {
      let feature = null;
      if (regionId) {
        feature =
          vectorFeatures.find((f) => {
            const featureRegionId =
              type === "warnMap"
                ? f.getProperties().fire_region_id
                : f.getProperties().id;
            return featureRegionId === regionId;
          }) ?? null;
      }
      setSelection(feature);
    },
    [type, vectorFeatures, setSelection]
  );

  return (
    <React.Fragment>
      {vectorError || textError || !geojson ? (
        <div>{t("error")}</div>
      ) : (
        <div className="container-fluid">
          <div className="map">
            <div className="map-image">
              <SwissMap
                legendLink={legendLink}
                vectorLayer={vectorLayer}
                textLayer={textLayer}
                background={background || ""}
                type={type}
                producedAt={new Date(producedAt || "")}
                onClick={handleSelectFeature}
                onHover={handleHoverFeature}
                onInitialized={handleMapInitialized}
              />
            </div>
            <div className="map-navigator">
              <MapNavigator
                type={type}
                cantons={cantons}
                features={vectorFeatures}
                measuresJsonPath={measuresJsonPath}
                selection={selection}
                onSelectionChange={handleNavigatorSelectionChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col col-md-12">
              <FireHistory
                type={type}
                cantons={cantons}
                regionsPath={regionsPath}
                filterPath={filterPath}
              />
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
